// Notification Service for Zielplanungs-Kalender
// Extended with backend synchronization and activity tracking
import { showLocalNotification, requestNotificationPermission, refreshFCMToken } from './firebase';
import axios from 'axios';
import { userStorage } from '../utils/userStorage';

const STORAGE_KEY = 'zielplanung_notification_settings';
const SCHEDULED_KEY = 'zielplanung_scheduled_notifications';

// Get API URL from environment
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Default settings - matches backend defaults
const defaultSettings = {
  enabled: false,
  appointment_reminder: true,
  reminder_minutes: 15,
  daily_overview: true,
  daily_overview_time: '07:00',
  focus_reminder: true,
  focus_reminder_time: '07:00',
  weekly_planning: true,
  weekly_planning_time: '19:00',
  monthly_planning: true,
  monthly_planning_day: 1,
  monthly_planning_time: '10:00',
  motivational_quote: true,
  motivational_quote_time: '08:00',
  streak_notification: true,
  inactivity_reminder: true
};

// Load notification settings from localStorage
export function loadNotificationSettings() {
  try {
    const saved = userStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
}

// Save notification settings to localStorage
export function saveNotificationSettings(settings) {
  userStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

// Active timeouts for scheduled notifications
let activeTimeouts = new Map();

// Schedule notification for an appointment
export function scheduleAppointmentReminder(appointment, reminderMinutes = 15) {
  const { id, title, date, startTime } = appointment;
  
  // Parse appointment datetime
  const [hours, minutes] = startTime.split(':').map(Number);
  const appointmentDate = new Date(date);
  appointmentDate.setHours(hours, minutes, 0, 0);
  
  // Calculate reminder time
  const reminderTime = new Date(appointmentDate.getTime() - (reminderMinutes * 60 * 1000));
  const now = new Date();
  
  // Don't schedule if reminder time is in the past
  if (reminderTime <= now) {
    return null;
  }
  
  const delay = reminderTime.getTime() - now.getTime();
  
  // Cancel existing reminder for this appointment
  cancelAppointmentReminder(id);
  
  // Schedule new reminder
  const timeoutId = setTimeout(() => {
    showLocalNotification(`⏰ ${title}`, {
      body: `Beginnt in ${reminderMinutes} Minuten (${startTime})`,
      tag: `appointment-${id}`,
      data: { appointmentId: id }
    });
    activeTimeouts.delete(id);
  }, delay);
  
  activeTimeouts.set(id, timeoutId);
  
  console.log(`Reminder scheduled for "${title}" at ${reminderTime.toLocaleTimeString()}`);
  return timeoutId;
}

// Cancel a scheduled appointment reminder
export function cancelAppointmentReminder(appointmentId) {
  const timeoutId = activeTimeouts.get(appointmentId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    activeTimeouts.delete(appointmentId);
  }
}

// Schedule reminders for all appointments today and tomorrow
export function scheduleAllReminders(appointments, reminderMinutes = 15) {
  const settings = loadNotificationSettings();
  if (!settings.enabled || !settings.appointment_reminder) {
    console.log('Appointment notifications are disabled');
    return;
  }
  
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  
  // Filter appointments for today and tomorrow
  const upcomingAppointments = appointments.filter(apt => 
    apt.date === today || apt.date === tomorrowStr
  );
  
  // Clear all existing timeouts
  activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  activeTimeouts.clear();
  
  // Schedule reminders
  let scheduledCount = 0;
  upcomingAppointments.forEach(apt => {
    if (scheduleAppointmentReminder(apt, reminderMinutes)) {
      scheduledCount++;
    }
  });
  
  console.log(`Scheduled ${scheduledCount} appointment reminders`);
  return scheduledCount;
}

// Initialize notifications - called on app start
export async function initializeNotifications() {
  const settings = loadNotificationSettings();
  
  if (settings.enabled) {
    const permission = await requestNotificationPermission();
    if (permission) {
      // Track user activity
      trackUserActivity();
      
      // Refresh FCM token and update on server if changed (fixes PWA reinstall issue)
      try {
        const newToken = await refreshFCMToken();
        if (newToken) {
          const authToken = localStorage.getItem('token');
          if (authToken) {
            await axios.post(`${API}/notifications/subscribe`, 
              { fcm_token: newToken },
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            console.log('FCM token updated on server');
          }
        }
      } catch (error) {
        console.error('Error updating FCM token:', error);
      }
    }
    return permission !== null;
  }
  
  return false;
}

// Request permission and enable notifications
export async function enableNotifications() {
  const permission = await requestNotificationPermission();
  
  if (permission) {
    const settings = loadNotificationSettings();
    settings.enabled = true;
    saveNotificationSettings(settings);
    
    // Track activity when enabling
    trackUserActivity();
    
    return true;
  }
  
  return false;
}

// Disable notifications
export function disableNotifications() {
  // Cancel all scheduled reminders
  activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  activeTimeouts.clear();
  
  const settings = loadNotificationSettings();
  settings.enabled = false;
  saveNotificationSettings(settings);
}

// Check if notifications are available and enabled
export function getNotificationStatus() {
  const settings = loadNotificationSettings();
  
  return {
    supported: 'Notification' in window,
    permission: 'Notification' in window ? Notification.permission : 'unavailable',
    enabled: settings.enabled,
    reminderMinutes: settings.reminder_minutes
  };
}

// Test notification
export function sendTestNotification() {
  showLocalNotification('🔔 Test-Benachrichtigung', {
    body: 'Benachrichtigungen funktionieren!',
    tag: 'test-notification'
  });
}

// ==================== USER ACTIVITY TRACKING ====================

// Track user activity for streak calculation
export async function trackUserActivity() {
  const token = localStorage.getItem('token');
  if (!token) return;
  
  try {
    const response = await axios.post(`${API}/notifications/activity`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const { streak } = response.data;
    
    // Show local streak notification for milestones
    if (streak === 7) {
      showLocalNotification('🔥 Eine Woche geschafft!', {
        body: '7 Tage in Folge aktiv – du bist auf dem richtigen Weg!',
        tag: 'streak-7'
      });
    } else if (streak === 30) {
      showLocalNotification('🔥 30 Tage Streak!', {
        body: 'Unglaublich! Einen ganzen Monat lang jeden Tag dabei!',
        tag: 'streak-30'
      });
    }
    
    return response.data;
  } catch (error) {
    console.log('Activity tracking not available:', error.message);
    return null;
  }
}

// Get current streak
export async function getUserStreak() {
  const token = localStorage.getItem('token');
  if (!token) return { streak: 0, last_active: null };
  
  try {
    const response = await axios.get(`${API}/notifications/streak`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    console.log('Could not fetch streak:', error.message);
    return { streak: 0, last_active: null };
  }
}

// ==================== SYNC SETTINGS WITH BACKEND ====================

export async function syncSettingsWithBackend(settings) {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    await axios.put(`${API}/notifications/settings`, settings, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return true;
  } catch (error) {
    console.error('Could not sync settings:', error.message);
    return false;
  }
}

export async function loadSettingsFromBackend() {
  const token = localStorage.getItem('token');
  if (!token) return loadNotificationSettings();
  
  try {
    const response = await axios.get(`${API}/notifications/settings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // Merge with local settings and save
    const mergedSettings = { ...loadNotificationSettings(), ...response.data };
    saveNotificationSettings(mergedSettings);
    return mergedSettings;
  } catch (error) {
    console.log('Loading local settings, backend not available');
    return loadNotificationSettings();
  }
}

// ==================== SYNC APPOINTMENTS FOR SERVER-SIDE REMINDERS ====================

/**
 * Sync OWN appointments (not Google imports) to backend for server-side push reminders.
 * Works when app is closed. Syncs next 7 days.
 * 
 * Backend structure: 1 doc per day, grouped by reminder time
 * { "_id": "2026-01-08", "0845": ["user1§Meeting§0900"], "1730": ["user1§Arzt§1800"] }
 */
export async function syncAppointmentsWithBackend(appointments) {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  const settings = loadNotificationSettings();
  if (!settings.enabled || !settings.appointment_reminder) {
    console.log('Appointment reminders disabled, skipping sync');
    return false;
  }
  
  try {
    // Get today and next 7 days
    const now = new Date();
    const weekLater = new Date(now);
    weekLater.setDate(weekLater.getDate() + 7);
    
    const todayStr = now.toISOString().split('T')[0];
    const weekLaterStr = weekLater.toISOString().split('T')[0];
    
    // Filter: Only OWN appointments (NOT Google Calendar imports) for next 7 days
    const ownUpcomingAppointments = appointments.filter(apt => {
      // Skip Google Calendar imports
      if (apt.isGoogleEvent) return false;
      
      // Only next 7 days
      const aptDate = apt.date;
      return aptDate >= todayStr && aptDate <= weekLaterStr;
    });
    
    // Always sync (even if empty - to clear old reminders when user deletes all appointments)
    const response = await axios.post(`${API}/notifications/appointments`, ownUpcomingAppointments, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    console.log(`Synced ${response.data.count} own appointment reminders (excluding Google imports)`);
    return true;
  } catch (error) {
    console.error('Could not sync appointments:', error.message);
    return false;
  }
}

/**
 * Sync focus texts for today and tomorrow to backend.
 * This ensures push notifications have the correct focus even if user doesn't login that day.
 * 
 * Stored in notification_settings: { "focus_schedule": { "2026-01-12": "Fokus A", "2026-01-13": "Fokus B" } }
 * Storage: ~100 bytes per user (2 days × ~50 bytes)
 * 
 * @param {Object} dayFocus - Map of date -> focus text from localStorage
 * @returns {Promise<boolean>} - Success status
 */
export async function syncAllFocusWithBackend(dayFocus) {
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No auth token, skipping focus sync');
    return false;
  }
  
  try {
    // Only today and tomorrow
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    
    // Always include today and tomorrow keys, even if empty (to clear old values)
    const focusSchedule = {
      [today]: dayFocus[today] || "",
      [tomorrowStr]: dayFocus[tomorrowStr] || ""
    };
    
    // Also clear legacy daily_focus field to prevent fallback to old data
    await axios.put(`${API}/notifications/settings`, 
      { 
        focus_schedule: focusSchedule,
        daily_focus: dayFocus[today] || ""  // Sync legacy field too
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    console.log(`Focus synced: today="${focusSchedule[today]}", tomorrow="${focusSchedule[tomorrowStr]}"`);
    return true;
  } catch (error) {
    console.error('Could not sync focus schedule:', error.message);
    return false;
  }
}

/**
 * Sync daily focus text to backend for personalized focus reminder notifications.
 * Only syncs today's focus - minimal storage (1 field per user in notification_settings).
 * 
 * @param {string} focusText - The focus text for today
 * @returns {Promise<boolean>} - Success status
 */
export async function syncDailyFocusWithBackend(focusText) {
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No auth token, skipping focus sync');
    return false;
  }
  
  try {
    await axios.put(`${API}/notifications/settings`, 
      { daily_focus: focusText || '' },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    console.log('Daily focus synced to backend:', focusText ? `"${focusText.substring(0, 30)}..."` : '(cleared)');
    return true;
  } catch (error) {
    console.error('Could not sync daily focus:', error.message);
    return false;
  }
}

